/**
 * Google Analytics 4 with Consent Mode v2.
 *
 * Nothing is sent until the visitor accepts analytics cookies in the banner.
 * Consent defaults to denied, so the tag is loaded in "cookieless ping" mode
 * and upgraded to full measurement once consent is granted.
 */

export const GA_MEASUREMENT_ID = "G-ZD8FVLGQEL";
export const CONSENT_STORAGE_KEY = "behot_cookie_consent";
export const CONSENT_VERSION = "1";

export type ConsentChoice = "granted" | "denied";

declare global {
  interface Window {
    dataLayer?: IArguments[];
    gtag?: (...args: unknown[]) => void;
  }
}

/**
 * Buffers commands issued before gtag.js is loaded. The tag is booted on the
 * first interaction (or after a few idle seconds) to keep it off the hydration
 * critical path, so early page views would otherwise be lost.
 */
const preboot: unknown[][] = [];

function pushGtag(...args: unknown[]) {
  if (typeof window === "undefined") return;
  if (!initialised) {
    if (preboot.length < 50) preboot.push(args);
    return;
  }
  window.gtag?.(...args);
}


export function readStoredConsent(): ConsentChoice | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { choice?: string; version?: string };
    if (parsed.version !== CONSENT_VERSION) return null;
    return parsed.choice === "granted" ? "granted" : "denied";
  } catch {
    return null;
  }
}

export function storeConsent(choice: ConsentChoice) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      CONSENT_STORAGE_KEY,
      JSON.stringify({ choice, version: CONSENT_VERSION, at: new Date().toISOString() }),
    );
  } catch {
    /* storage blocked — consent stays session-only */
  }
}

let initialised = false;

/** Load gtag.js once, with consent defaulting to denied. */
export function initAnalytics() {
  if (typeof window === "undefined" || initialised) return;
  initialised = true;

  const stored = readStoredConsent();

  // The Google tag does not create this dispatcher itself. The official
  // snippet defines it before loading gtag.js so queued commands are consumed.
  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer?.push(arguments);
  };

  pushGtag("consent", "default", {
    ad_storage: stored ?? "denied",
    ad_user_data: stored ?? "denied",
    ad_personalization: stored ?? "denied",
    analytics_storage: stored ?? "denied",
    functionality_storage: "granted",
    security_storage: "granted",
    wait_for_update: 500,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);

  pushGtag("js", new Date());
  pushGtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    currency: "USD",
    anonymize_ip: true,
  });

  // Replay anything recorded before the tag existed (first page view, etc.).
  for (const args of preboot.splice(0)) window.gtag?.(...args);

  if (stored === "granted") applyConsent("granted");

}

/** Update Consent Mode after the visitor chooses in the banner. */
export function applyConsent(choice: ConsentChoice) {
  const allGranted = choice === "granted";
  pushGtag("consent", "update", {
    analytics_storage: choice,
    ad_storage: allGranted ? "granted" : "denied",
    ad_user_data: allGranted ? "granted" : "denied",
    ad_personalization: allGranted ? "granted" : "denied",
  });
  pushGtag("event", "consent_choice", { consent_state: choice, all_granted: allGranted });

  // The initial SPA page view can occur while consent is still denied. Send a
  // consented view now so a visitor's first page is visible in Realtime.
  if (allGranted && typeof window !== "undefined") {
    trackPageView(`${window.location.pathname}${window.location.search}`);
  }
}

/** Send a GA4 event. Safe to call before consent — GA queues/drops it itself. */
export function track(event: string, params: Record<string, unknown> = {}) {
  if (typeof window === "undefined") return;
  pushGtag("event", event, params);
}

export function trackPageView(path: string, title?: string) {
  if (typeof window === "undefined") return;
  pushGtag("event", "page_view", {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
}

/** Identify the signed-in visitor (no PII — the user id only). */
export function setAnalyticsUser(userId: string | null, props: Record<string, unknown> = {}) {
  pushGtag("set", { user_id: userId ?? undefined, user_properties: props });
}

/* ---------------------------------------------------------------- *
 * Domain helpers — one place for every commerce / lead / engagement *
 * ---------------------------------------------------------------- */

export const analytics = {
  signUpStarted: (method: string) => track("sign_up_started", { method }),
  signUp: (method: string) => track("sign_up", { method }),
  login: (method: string) => track("login", { method }),

  viewListing: (p: { slug: string; name: string; category?: string | undefined; heat?: number | undefined }) =>
    track("view_item", {
      item_id: p.slug,
      item_name: p.name,
      item_category: p.category,
      value: p.heat,
      currency: "USD",
    }),
  listingLinkClick: (p: { slug: string; url: string }) =>
    track("listing_link_click", { item_id: p.slug, link_url: p.url, outbound: true }),

  beginCheckout: (p: {
    kind: "boost" | "topup" | "listing" | "pro" | "offer" | "listing_edit";
    valueUsd: number;
    itemId?: string | undefined;
    category?: string | undefined;
  }) =>
    track("begin_checkout", {
      currency: "USD",
      value: p.valueUsd,
      checkout_kind: p.kind,
      items: [{ item_id: p.itemId ?? p.kind, item_name: p.kind, item_category: p.category }],
    }),

  purchase: (p: { transactionId: string; kind: string; valueUsd: number }) =>
    track("purchase", {
      transaction_id: p.transactionId,
      currency: "USD",
      value: p.valueUsd,
      checkout_kind: p.kind,
      items: [{ item_id: p.kind, item_name: p.kind, quantity: 1, price: p.valueUsd }],
    }),

  lead: (p: { source: string; detail?: string | undefined }) =>
    track("generate_lead", { lead_source: p.source, lead_detail: p.detail, currency: "USD" }),

  share: (p: { method: string; contentType: string; itemId?: string | undefined }) =>
    track("share", { method: p.method, content_type: p.contentType, item_id: p.itemId }),

  referralVisit: (code: string) => track("referral_visit", { referral_code: code }),
  withdrawalRequested: (p: { valueUsd: number; method: string }) =>
    track("withdrawal_requested", { value: p.valueUsd, currency: "USD", payout_method: p.method }),

  action: (name: string, params: Record<string, unknown> = {}) => track(name, params),
};

/**
 * Global, low-noise auto-capture: outbound links, downloads, and any element
 * carrying data-analytics="event_name" (plus data-analytics-* params).
 */
const PENDING_KEY = "behot_pending_checkout";

/** Remember what is being bought so the purchase event has a value on return. */
export function rememberPendingCheckout(kind: string, valueUsd: number) {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ kind, valueUsd }));
  } catch {
    /* ignore */
  }
}

export function takePendingCheckout(): { kind: string; valueUsd: number } | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(PENDING_KEY);
    window.sessionStorage.removeItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as { kind: string; valueUsd: number }) : null;
  } catch {
    return null;
  }
}

export function installAutoTracking() {
  if (typeof window === "undefined") return () => undefined;

  const onClick = (event: MouseEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target) return;

    const tagged = target.closest<HTMLElement>("[data-analytics]");
    if (tagged) {
      const name = tagged.dataset["analytics"]!;
      const params: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(tagged.dataset)) {
        if (key.startsWith("analytics") && key !== "analytics" && value != null) {
          const param = key.slice("analytics".length);
          params[param.charAt(0).toLowerCase() + param.slice(1)] = value;
        }
      }
      track(name, params);
      return;
    }

    const anchor = target.closest<HTMLAnchorElement>("a[href]");
    if (!anchor) return;
    const href = anchor.getAttribute("href") ?? "";
    if (href.startsWith("mailto:")) {
      track("contact_email_click", { link_url: href });
      return;
    }
    if (/^https?:/i.test(href) && !href.includes(window.location.host)) {
      track("click", {
        link_url: href,
        link_domain: (() => {
          try {
            return new URL(href).hostname;
          } catch {
            return undefined;
          }
        })(),
        link_text: anchor.textContent?.trim().slice(0, 80),
        outbound: true,
      });
    }
  };

  document.addEventListener("click", onClick, { capture: true });
  return () => document.removeEventListener("click", onClick, { capture: true } as EventListenerOptions);
}
